import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils.js";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        // Solid primary stays: it is the one fill whose white foreground is
        // contrast-verified in both themes (see W1 contrast script). Statuses
        // can't do the same — in dark they are text-grade tokens, so a solid
        // fill would need a per-theme foreground swap.
        default:
          "border-transparent bg-primary text-primary-foreground",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground",
        // Tinted-bg pattern: *-bg token wash + the status token as text +
        // a subtle same-hue border. The status DEFAULTs are tuned to ≥4.5:1
        // on their own tint in both themes, unlike the old solid saturated
        // fills whose white text failed AA in light.
        destructive: "border-destructive/25 bg-destructive-bg text-destructive",
        success: "border-success/25 bg-success-bg text-success",
        warning: "border-warning/25 bg-warning-bg text-warning",
        outline: "border-border text-foreground",
      },
    },
    defaultVariants: {
      variant: "outline",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export type StatusBadgeProps = BadgeProps;

/** Semantic operational state. Status is always communicated in text. */
export function StatusBadge(props: StatusBadgeProps): React.ReactElement {
  return <Badge {...props} />;
}

export interface TagProps extends React.HTMLAttributes<HTMLSpanElement> {
  color?: string;
}

/** User-authored classification; custom color is a non-text marker only. */
export function Tag({ color, className, children, ...props }: TagProps): React.ReactElement {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border bg-background px-2 py-0.5 text-xs font-medium text-foreground",
        className,
      )}
      {...props}
    >
      {color ? (
        <span
          aria-hidden
          className="h-2 w-2 shrink-0 rounded-full border border-foreground/20"
          style={{ backgroundColor: color }}
        />
      ) : null}
      {children}
    </span>
  );
}

export interface CountBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  count: number;
  max?: number;
  label: string;
}

/** Compact actionable count with a full accessible category label. */
export function CountBadge({
  count,
  max = 99,
  label,
  className,
  ...props
}: CountBadgeProps): React.ReactElement | null {
  if (count <= 0) return null;
  const visible = count > max ? `${max}+` : String(count);
  return (
    <span
      className={cn(
        "inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-xs font-semibold tabular-nums text-primary-foreground",
        className,
      )}
      {...props}
    >
      <span aria-hidden>{visible}</span>
      <span className="sr-only">{count} {label}</span>
    </span>
  );
}

export { Badge, badgeVariants };
